import { ref, computed } from 'vue'
import { useOrder } from '@core/composables/order/useOrder'
import moment from 'moment-timezone'
import { useHttp } from '@core/composables/useHttp'
import API from '@core/api'
const getBlackoutDates = () => {
  const blackoutDates = ref([])
  // Flips only once the list is populated (stays false if the request fails),
  // so consumers can wait for it without relying on the request's timing.
  const isBlackoutDatesReady = ref(false)

  const { isLoading, execute } = useHttp()

  execute({ params: {}, url: API.blackoutDates }, ({ data }) => {
    blackoutDates.value = formatBlackoutDates(data)
    isBlackoutDatesReady.value = true
  })

  return {
    blackoutDates,
    isBlackoutDatesLoading: isLoading,
    isBlackoutDatesReady,
  }
}

const formatBlackoutDates = (blackoutDates) => {
  return blackoutDates?.map((date) => {
    const tm = moment(date.date, 'YYYY-MM-DD', true).format('YYYY-MM-DD')
    return tm
  })
}

export const useSchedule = () => {
  const distributionDate = ref(null)
  const { order, useUpdateOrder } = useOrder()
  const { updateOrder } = useUpdateOrder()

  const isSameDistributionDate = computed(() => {
    if (!distributionDate.value) return false

    return moment(distributionDate.value).isSame(
      moment.tz('America/New_York').format('YYYY-MM-DD'),
      'day'
    )
  })

  // Saves cannot be cancelled, so quick successive picks can have several in
  // flight. Only the latest one may touch the selection when it comes back,
  // and a rejection falls back to the last date the server is known to hold.
  let latestSave = 0
  let savesInFlight = 0
  let lastKnownServerDate = null

  // `date` is either a Date at UTC midnight of the picked day (the picker
  // runs with timezone="UTC") or a YYYY-MM-DD string; reading it in UTC
  // yields that calendar day regardless of the browser's zone or DST.
  // `onSaved(date)` runs once the server has accepted the new date.
  const onSelect = (date, onSaved = () => {}) => {
    const filteredDate = date ? moment.utc(date).format('YYYY-MM-DD') : date

    // The picker echoes programmatic values back through this handler.
    if (filteredDate === distributionDate.value) return

    if (savesInFlight === 0) lastKnownServerDate = distributionDate.value
    distributionDate.value = filteredDate

    if (!date) return

    const save = ++latestSave
    savesInFlight++

    updateOrder(
      { distributionDate: filteredDate },
      () => {
        savesInFlight--
        if (save !== latestSave) return

        lastKnownServerDate = filteredDate
        onSaved(filteredDate)
      },
      () => {
        // Rejected (e.g. a blackout date the calendar could not know about):
        // do not leave a date on screen that will be rejected again on confirm.
        savesInFlight--
        if (save !== latestSave) return

        distributionDate.value = lastKnownServerDate
      }
    )
  }

  if (order && order?.value?.distributionDate) {
    const normalizedDate = moment(order.value.distributionDate).format(
      'YYYY-MM-DD'
    )
    distributionDate.value = normalizedDate
  }

  return {
    distributionDate,
    isSameDistributionDate,
    onSelect,
    getBlackoutDates,
  }
}
